import { useState, useCallback, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { firestore, auth } from '../../firebase';
import { format, eachDayOfInterval } from 'date-fns';

export const useAnalyticsData = (orgId) => {
    // Cache for already fetched daily data - keyed by ISO date string
    const cache = useRef(new Map());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    /**
     * Wait for authentication token to be ready with custom claims
     * This prevents the race condition where analytics data is fetched
     * before custom claims are propagated to the client
     */
    const waitForAuthReady = useCallback(async () => {
        if (!auth.currentUser) {
            throw new Error('User not authenticated');
        }
        
        try {
            // Force token refresh to ensure custom claims are loaded
            await auth.currentUser.getIdToken(true);
            
            // Add a small delay to ensure custom claims have propagated
            // to Firestore security rules (Firebase can take a moment to sync)
            await new Promise(resolve => setTimeout(resolve, 100));
            
            return true;
        } catch (err) {
            console.error('Error refreshing auth token:', err);
            throw err;
        }
    }, []);
    
    /**
     * Fetch analytics data for a specific date range
     * Only fetches dates that aren't already cached
     */
    const fetchDateRange = useCallback(async (startDate, endDate) => {
        if (!orgId || !startDate || !endDate) {
            return {};
        }
        
        setLoading(true);
        setError(null);
        
        try {
            // Wait for auth token to be ready before fetching data
            await waitForAuthReady();
            
            // Get all dates in the range
            const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
            const dateStrings = dateRange.map(date => format(date, 'yyyy-MM-dd'));
            
            // Find dates we haven't cached yet
            const uncachedDates = dateStrings.filter(dateStr => !cache.current.has(dateStr));
            
            // console.log(`Fetching analytics: ${uncachedDates.length} new dates out of ${dateStrings.length} total`);
            
            // Fetch uncached dates in parallel
            const fetchPromises = uncachedDates.map(async (dateStr) => {
                // Retry logic for handling any remaining timing issues
                const maxRetries = 2;
                let lastError = null;
                
                for (let attempt = 0; attempt <= maxRetries; attempt++) {
                    try {
                        const docRef = doc(firestore, 'dailyAgg', orgId, 'days', dateStr);
                        const docSnap = await getDoc(docRef);
                        
                        if (docSnap.exists()) {
                            const data = docSnap.data();
                            cache.current.set(dateStr, data);
                            return { date: dateStr, data, success: true };
                        } else {
                            // Cache empty result to avoid refetching
                            cache.current.set(dateStr, {});
                            return { date: dateStr, data: {}, success: true };
                        }
                    } catch (err) {
                        lastError = err;
                        
                        // If it's a permissions error and we haven't exhausted retries, wait and try again
                        if (err.code === 'permission-denied' && attempt < maxRetries) {
                            console.warn(`Permission denied for analytics on attempt ${attempt + 1}, retrying in 200ms...`);
                            await new Promise(resolve => setTimeout(resolve, 200));
                            continue;
                        }
                        
                        console.error(`Error fetching analytics for ${dateStr} (attempt ${attempt + 1}):`, err);
                        break;
                    }
                }
                
                // Don't cache errors, allow retry
                return { date: dateStr, data: {}, success: false, error: lastError };
            });
            
            const fetchResults = await Promise.all(fetchPromises);
            
            // Check for any fetch errors
            const errors = fetchResults.filter(result => !result.success);
            if (errors.length > 0) {
                console.warn(`Failed to fetch ${errors.length} dates:`, errors);
            }
            
            // Return all requested dates from cache
            const result = {};
            dateStrings.forEach(dateStr => {
                result[dateStr] = cache.current.get(dateStr) || {};
            });
            
            return result;
        } catch (err) {
            console.error('Error in fetchDateRange:', err);
            setError(err);
            return {};
        } finally {
            setLoading(false);
        }
    }, [orgId, waitForAuthReady]);
    
    /**
     * Aggregate data across multiple QRs and dates
     * Takes raw daily data and returns aggregated analytics
     */
    const aggregateData = useCallback((dailyData, qrIds = null) => {
        const aggregated = {
            totalScans: 0,
            totalUniqueIps: 0,
            qrBreakdown: {},
            dailyTotals: {},
            geoData: {
                countries: {},
                regions: {},
                cities: {},
                geoHashes: {}
            },
            deviceData: {
                types: {},
                brands: {},
                models: {},
                browsers: {},
                os: {}
            }
        };
        
        // Process each day's data
        Object.entries(dailyData).forEach(([dateStr, dayData]) => {
            let dayScans = 0;
            let dayUniqueIps = 0;
            const dayUniqueIpSet = new Set();
            
            // Process each QR's data for this day
            Object.entries(dayData).forEach(([qrId, qrData]) => {
                // Skip if we're filtering to specific QR IDs and this isn't one of them
                if (qrIds && !qrIds.includes(qrId)) {
                    return;
                }
                
                // Initialize QR breakdown if not exists
                if (!aggregated.qrBreakdown[qrId]) {
                    aggregated.qrBreakdown[qrId] = {
                        scans: 0,
                        uniqueIps: 0,
                        countries: {},
                        regions: {},
                        cities: {}
                    };
                }
                
                // Add scans and unique IPs
                const qrScans = qrData.scans || 0;
                const qrUniqueIps = qrData.uniqueIps || 0;
                
                aggregated.qrBreakdown[qrId].scans += qrScans;
                aggregated.qrBreakdown[qrId].uniqueIps += qrUniqueIps;
                
                dayScans += qrScans;
                dayUniqueIps += qrUniqueIps;
                
                // Aggregate geographic data
                if (qrData.country) {
                    Object.entries(qrData.country).forEach(([country, count]) => {
                        aggregated.geoData.countries[country] = (aggregated.geoData.countries[country] || 0) + count;
                        aggregated.qrBreakdown[qrId].countries[country] = (aggregated.qrBreakdown[qrId].countries[country] || 0) + count;
                    });
                }
                
                if (qrData.region) {
                    Object.entries(qrData.region).forEach(([region, count]) => {
                        aggregated.geoData.regions[region] = (aggregated.geoData.regions[region] || 0) + count;
                        aggregated.qrBreakdown[qrId].regions[region] = (aggregated.qrBreakdown[qrId].regions[region] || 0) + count;
                    });
                }
                
                if (qrData.city) {
                    Object.entries(qrData.city).forEach(([city, count]) => {
                        aggregated.geoData.cities[city] = (aggregated.geoData.cities[city] || 0) + count;
                        aggregated.qrBreakdown[qrId].cities[city] = (aggregated.qrBreakdown[qrId].cities[city] || 0) + count;
                    });
                }
                
                if (qrData.geoHash) {
                    Object.entries(qrData.geoHash).forEach(([geoHash, count]) => {
                        aggregated.geoData.geoHashes[geoHash] = (aggregated.geoData.geoHashes[geoHash] || 0) + count;
                    });
                }
                
                // Aggregate device data
                if (qrData.device) {
                    Object.entries(qrData.device).forEach(([key, count]) => {
                        if (key.startsWith('type_')) {
                            const type = key.replace('type_', '');
                            aggregated.deviceData.types[type] = (aggregated.deviceData.types[type] || 0) + count;
                        } else if (key.startsWith('brand_')) {
                            const brand = key.replace('brand_', '');
                            aggregated.deviceData.brands[brand] = (aggregated.deviceData.brands[brand] || 0) + count;
                        } else if (key.startsWith('model_')) {
                            const model = key.replace('model_', '');
                            aggregated.deviceData.models[model] = (aggregated.deviceData.models[model] || 0) + count;
                        } else if (key.startsWith('browser_')) {
                            const browser = key.replace('browser_', '');
                            aggregated.deviceData.browsers[browser] = (aggregated.deviceData.browsers[browser] || 0) + count;
                        } else if (key.startsWith('os_')) {
                            const os = key.replace('os_', '');
                            aggregated.deviceData.os[os] = (aggregated.deviceData.os[os] || 0) + count;
                        }
                    });
                }
            });
            
            // Store daily totals
            aggregated.dailyTotals[dateStr] = {
                scans: dayScans,
                uniqueIps: dayUniqueIps
            };
            
            aggregated.totalScans += dayScans;
            aggregated.totalUniqueIps += dayUniqueIps;
        });
        
        return aggregated;
    }, []);
    
    /**
     * Get cached data for specific dates (without fetching)
     */
    const getCachedData = useCallback((dateStrings) => {
        const result = {};
        dateStrings.forEach(dateStr => {
            if (cache.current.has(dateStr)) {
                result[dateStr] = cache.current.get(dateStr);
            }
        });
        return result;
    }, []);
    
    /**
     * Check if specific dates are cached
     */
    const areDatesCached = useCallback((dateStrings) => {
        return dateStrings.every(dateStr => cache.current.has(dateStr));
    }, []);
    
    /**
     * Clear the cache
     */
    const clearCache = useCallback(() => {
        cache.current.clear();
        console.log('Analytics cache cleared');
    }, []);
    
    /**
     * Get cache statistics
     */
    const getCacheStats = useCallback(() => {
        return {
            size: cache.current.size,
            dates: Array.from(cache.current.keys()).sort()
        };
    }, []);
    
    return {
        fetchDateRange,
        aggregateData,
        getCachedData,
        areDatesCached,
        clearCache,
        getCacheStats,
        loading,
        error
    };
}; 